import React from 'react';
import './ContentPage.css'; // Import the CSS file
import { Link, useParams } from 'react-router-dom';
import { teamMates } from './teamData'; // Import teamMates data
import { useProject } from '../hooks/useProjects';
import Footer from './Footer';

function ContentPage() {
  const { targetSection } = useParams();
  const { project, loading, error } = useProject(targetSection);

  if (loading) {
    return (
      <div className="content-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-page">
        <div className="error-container">
          <p>Error loading project: {error.message}</p>
          <Link to="/projects" className="back-link">
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="content-page">
        <div className="error-container">
          <h2>Project not found</h2>
          <p>The project "{targetSection}" could not be found in the database.</p>
          <Link to="/projects" className="back-link">
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  const collaborators = project.Collaborators.split(',').map((name) => name.trim());

  const heroImage = project.pictureSrc.startsWith('http')
    ? project.pictureSrc
    : process.env.PUBLIC_URL + '/images/' + project.pictureSrc;

  // Function to parse markdown links and convert them to HTML
  const parseMarkdownLinks = (text) => {
    if (!text) return '';

    // Regular expression to match markdown links: [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

    // Split the text by links and process each part
    const parts = text.split(linkRegex);
    const result = [];

    for (let i = 0; i < parts.length; i += 3) {
      // Add text before the link
      if (parts[i]) {
        result.push(parts[i]);
      }

      // Add the link if it exists
      if (parts[i + 1] && parts[i + 2]) {
        result.push(
          <a
            key={`link-${i}`}
            href={parts[i + 2]}
            target="_blank"
            rel="noopener noreferrer"
            className="content-inline-link"
          >
            {parts[i + 1]}
          </a>
        );
      }
    }

    return result.length > 0 ? result : text;
  };

  return (
    <div className="content-page">
      {/* Same opening as every other detail page. Deliberately without a
          background image: the project figure is usually data rather than
          decoration, so it is shown properly in the body instead of being
          darkened behind the title and then repeated. */}
      <header className="page-hero page-hero--plain">
        <div className="page-hero__inner">
          <p className="page-hero__eyebrow">Research</p>
          <h1 className="page-hero__title">{project.title}</h1>
        </div>
      </header>

      <div className="content-text page-body">
        <Link to="/projects" className="back-link">
          ← Back to Projects
        </Link>

        {/* Sections are spreads — label pinned in the margin, content beside
            it — the same composition the main site's pages use. */}
        <section className="u-spread detail-section">
          <h2 className="u-spread-label">{project.title2 || 'Objective'}</h2>
          <div className="detail-section__body">
            <p className="Description Description--lede">{project.objective}</p>
          </div>
        </section>

        <figure className="detail-figure">
          <img src={heroImage} alt={project.title} className="contentmain-img" />
        </figure>

        {project.targetSection === 'city_of_austin' && (
          <section className="u-spread detail-section partners-section">
            <h2 className="u-spread-label">Partners</h2>
            <div className="detail-section__body">
              <div className="partners-logos">
                <div className="partner-logo">
                  <img
                    src={process.env.PUBLIC_URL + "/images/f_nist-logo-brand-black.png"}
                    alt="NIST Logo"
                    className="partner-logo-img"
                  />
                </div>
                <div className="partner-logo">
                  <a
                    href="https://utcitycolab.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="partner-logo-link"
                  >
                    <img
                      src={process.env.PUBLIC_URL + "/images/colab_logo.png"}
                      alt="UT City CoLab Logo"
                      className="partner-logo-img"
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {project.title3 && (
          <section className="u-spread detail-section">
            <h2 className="u-spread-label">{project.title3}</h2>
            <div className="detail-section__body">
              {project.longContent1 && <p className="Description">{parseMarkdownLinks(project.longContent1)}</p>}
              {project.longContent2 && <p className="Description">{parseMarkdownLinks(project.longContent2)}</p>}
              {project.longContent3 && <p className="Description">{parseMarkdownLinks(project.longContent3)}</p>}
            </div>
          </section>
        )}

        <section className="u-spread detail-section">
          <h2 className="u-spread-label">Collaborators</h2>
          <div className="detail-section__body">
            <div className="collabcard">
              {collaborators.map((collaborator, index) => {
                // Split the collaborator's name by whitespace
                const [firstName, lastName] = collaborator.split(' ');

                // Create the link using firstName and lastName
                const linkTo = `/Team/${firstName}_${lastName}`;

                // Find the corresponding team member
                const teamMember = teamMates.find(member => member.name === collaborator);

                // Use the team member's image if found, otherwise use a default image
                const imageSrc = teamMember ? teamMember.image : process.env.PUBLIC_URL + "/images/defaultpic.png";

                return (
                  <Link to={linkTo} key={index} className="collabcard-content">
                    <img src={imageSrc} alt={collaborator} />
                    <span className="collabcard-name">{collaborator}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default ContentPage;
